import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostBinding,
  OnInit,
  inject,
  afterNextRender,
  DOCUMENT,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';

import { PreMediaService } from '@app/features/pre-media/pre-media.service';
import { FooterComponent } from '@modules/base/footer/footer.component';
import { HeaderComponent } from '@modules/base/header/header.component';
import { ConsentService } from '@modules/consent/consent.service';
import { FavoritesModalComponent } from '@modules/favorites/favorites-modal/favorites-modal.component';
import { FavoritesContentService } from '@modules/favorites/services/favorites-content.service';
import { FavoritesService } from '@modules/favorites/services/favorites.service';
import { EventsService } from '@modules/shared/services/events.service';
import { GlobalGTMEventsService } from '@modules/shared/services/global-gtm-events.service';
import { LocaleService } from '@modules/shared/services/locale.service';
import { ScrollLockService } from '@modules/shared/services/scroll-lock.service';
import { ScrollToLastPositionService } from '@modules/shared/services/scroll-to-last-position.service';
import { BufferingComponent } from '@modules/ui/buffering/buffering.component';
import { environment } from 'src/environments/environment';
import { languageConfig } from 'src/environments/language.config';

import { AppService } from './app.service';
import { loadWorkbenchAfterHydration } from './workbench-loader';

@Component({
  selector: 'mb-root',
  imports: [CommonModule, FooterComponent, FavoritesModalComponent, HeaderComponent, BufferingComponent, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit {
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private consentService = inject(ConsentService);
  private scrollHistoryService = inject(ScrollToLastPositionService);
  private favoritesService = inject(FavoritesService);
  private favoritesContentService = inject(FavoritesContentService);
  private appService = inject(AppService);
  private preMediaService = inject(PreMediaService);
  private eventsService = inject(EventsService);
  private scrollLockService = inject(ScrollLockService);
  private globalGTMEventsService = inject(GlobalGTMEventsService);
  private localeService = inject(LocaleService);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  @HostBinding('class.floating-subnav')
  public floatingSubnav = false;

  @HostBinding('class.light-page')
  public isLigthPage = false;

  // only for testing frontend alignments
  public showGrid = false;
  public gridClass: string[] = ['container-xxl', 'container-sm'];
  public activeGridClass = 'container-sm';

  public favCount$: Observable<number>;
  public notificationCount$: Observable<number>;
  public favoritesOpen = false;
  public isLoading$: Observable<boolean>;

  public languageOptions: { label: string; value: string }[];
  public hasPreMedia$: Observable<boolean>;
  public preMediaCount$: Observable<number>;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      loadWorkbenchAfterHydration();
    }

    afterNextRender(() => {
      this.scrollHistoryService.handleScrollPositionRestoration();
      this.consentService.listenToConsentEvents();
      this.globalGTMEventsService.handleGlobalGTMEvents();
    });

    this.preMediaCount$ = this.preMediaService.preMediaCount$;
    this.hasPreMedia$ = this.preMediaService.hasPreMedia$;
    this.favoritesService.favoritesOpen$.subscribe((isOpen) => {
      this.favoritesOpen = isOpen;
      if (!isOpen) {
        this.scrollLockService.unlock();
      }
    });

    this.isLoading$ = this.appService.isLoading$;
    this.favCount$ = this.favoritesContentService.favoritesCount$;

    this.notificationCount$ = combineLatest(
      [this.preMediaCount$, this.eventsService.pendingRegistrationsCount$],
      (preMediaCount, pendingRegistrations) => {
        return preMediaCount + pendingRegistrations;
      }
    );

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.favoritesOpen = false;
      }

      if (event instanceof NavigationEnd) {
        this.localeService.syncUrlWithCurrentLanguage();
      }
    });

    this.translateService.addLangs(languageConfig.languages);
    this.languageOptions = this.translateService.getLangs().map((lang) => {
      return {
        label: lang,
        value: lang,
      };
    });

    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.updateHTMLLangAttribute(event.lang);
    });

    this.appService.isLightPage$.subscribe((isLightPage) => {
      this.isLigthPage = isLightPage;
    });

    this.initializeAppInsights();
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    window.mediaConfig = environment.mediaConfig;
  }

  private initializeAppInsights() {
    const connectionString = environment.connectionString;

    if (!connectionString) return;

    const angularPlugin = new AngularPlugin();
    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router },
        },
      },
    });

    try {
      appInsights.loadAppInsights();
      appInsights.addTelemetryInitializer((envelope) => {
        if (envelope && envelope.tags) {
          envelope.tags['ai.cloud.role'] = 'mbmedia_frontend';
        }
      });
    } catch {
      console.error('Error loading App Insights');
    }
  }

  private updateHTMLLangAttribute(lang: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.document.documentElement.lang = lang;
    }
  }

  public changeLang(lang: string) {
    this.localeService.changeLanguage(lang);
  }

  // IMPROVE: favorite service should close itself
  public closeFavorites() {
    this.scrollLockService.unlock();
    this.favoritesService.closeFavorites();
  }
}
